import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import useUser from "components/common/useUser";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { getItineraryPlannerTranslations } from "components/website/marketing/i18n/pages/itineraryPlanner";
import { ITINERARY_PLANNER_CONTENT } from "components/website/marketing/content/featurePageContent";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { useBusinessContext, useOrganisation } from "context/BusinessContext";
import React, { useState } from "react";
import { OsTypes, isMobile, osName } from "react-device-detect";
import { useAuthState } from "react-firebase-hooks/auth";
import useReactRouter from "use-react-router";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";
import firebase from "../../firebase";
import FAQSection from "../components/FAQSection";
import DownloadAppModal from "../components/DownloadAppModal";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import HowItWorksSection from "../components/HowItWorksSection";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import TestimonialsSection from "../components/TestimonialsSection";
import "../css/ItineraryPlanner.scss";
import "../css/Website.scss";
//@ts-ignore
import BackpackingVisual from "../img/screenshots/itineraryplanner/backpacking-trip.png";
import FamilyTripVisual from "../img/screenshots/itineraryplanner/family-trip.jpeg";
import FeatureRouteVisual from "../img/screenshots/itineraryplanner/feature-2.png";
import FeatureDayVisual from "../img/screenshots/itineraryplanner/feature-3.png";
import FeatureWebMobileVisual from "../img/screenshots/itineraryplanner/feature-4.png";
import FeatureGroupVisual from "../img/screenshots/itineraryplanner/feature-5.png";
import FeatureAiVisual from "../img/screenshots/itineraryplanner/feature-6.png";
import GroupTravelVisual from "../img/screenshots/itineraryplanner/group-travel.jpeg";
import PlannerMapVisual from "../img/screenshots/itineraryplanner/hero-image.svg";
import RoadTripVisual from "../img/screenshots/itineraryplanner/road-trip.jpeg";
//@ts-ignore
import Icon from "components/Icons/Icon";

// ── Feature card icons (teal stroke, matches Stippl palette) ──────────────────

const IconCalendar = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="26"
		height="26"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		stroke-width="1.75"
		stroke-linecap="round"
		stroke-linejoin="round"
	>
		<path d="M8 2v4"></path>
		<path d="M16 2v4"></path>
		<rect width="18" height="18" x="3" y="4" rx="2"></rect>
		<path d="M3 10h18"></path>
	</svg>
);
const IconList = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="26"
		height="26"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		stroke-width="1.75"
		stroke-linecap="round"
		stroke-linejoin="round"
	>
		<rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect>
		<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
		<path d="M12 11h4"></path>
		<path d="M12 16h4"></path>
		<path d="M8 11h.01"></path>
		<path d="M8 16h.01"></path>
	</svg>
);
const IconTrain = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="26"
		height="26"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		stroke-width="1.75"
		stroke-linecap="round"
		stroke-linejoin="round"
	>
		<rect width="16" height="16" x="4" y="3" rx="2"></rect>
		<path d="M4 11h16"></path>
		<path d="M12 3v8"></path>
		<path d="m8 19-2 3"></path>
		<path d="m18 22-2-3"></path>
		<path d="M8 15h.01"></path>
		<path d="M16 15h.01"></path>
	</svg>
);
const IconBuilding = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="26"
		height="26"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		stroke-width="1.75"
		stroke-linecap="round"
		stroke-linejoin="round"
	>
		<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
		<path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
		<path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
		<path d="M10 6h4"></path>
		<path d="M10 10h4"></path>
		<path d="M10 14h4"></path>
		<path d="M10 18h4"></path>
	</svg>
);
const IconLocation = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="26"
		height="26"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		stroke-width="1.75"
		stroke-linecap="round"
		stroke-linejoin="round"
	>
		<circle cx="12" cy="12" r="10"></circle>
		<circle cx="12" cy="12" r="6"></circle>
		<circle cx="12" cy="12" r="2"></circle>
	</svg>
);

// ── Bullet icons (white, on green rounded-square bg) ─────────────────────────

const IcoReorder = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="white"
		stroke-width="2"
		stroke-linecap="round"
		stroke-linejoin="round"
	>
		<path d="m21 16-4 4-4-4"></path>
		<path d="M17 20V4"></path>
		<path d="m3 8 4-4 4 4"></path>
		<path d="M7 4v16"></path>
	</svg>
);
const IcoInfinity = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="white"
		stroke-width="2"
		stroke-linecap="round"
		stroke-linejoin="round"
	>
		<circle cx="12" cy="12" r="10"></circle>
		<path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
		<path d="M2 12h20"></path>
	</svg>
);

const IcoPlane = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
		<path d="M14.5 1.5L1 6l5 3 2 6 3-5.5 3.5-8z" stroke="white" strokeWidth="1.4" strokeLinejoin="round" />
	</svg>
);
const IcoMonitor = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
		<rect x="1" y="2" width="14" height="10" rx="1.5" stroke="white" strokeWidth="1.5" />
		<path d="M5 14h6M8 12v2" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);
const IcoSync = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
		<path d="M2 8a6 6 0 0110.4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
		<path d="M14 8a6 6 0 01-10.4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
		<path d="M12 4l.5 2.5-2.5.5M4 12l-.5-2.5 2.5-.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);
const IcoPhone = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
		<rect x="4" y="1" width="8" height="14" rx="1.5" stroke="white" strokeWidth="1.5" />
		<path d="M7 12h2" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);
const IcoUsers = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
		<circle cx="6" cy="5" r="2.5" stroke="white" strokeWidth="1.5" />
		<path d="M1 13c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
		<path d="M12 7a2 2 0 100-4M15 13c0-1.66-1.34-3-3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);
const IcoStar = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
		<path d="M8 1l1.8 3.8 4.2.4-3 2.9.8 4.1L8 10.1l-3.8 2.1.8-4.1-3-2.9 4.2-.4L8 1z" stroke="white" strokeWidth="1.3" strokeLinejoin="round" />
	</svg>
);

const IcoCheck = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
		<circle cx="10" cy="10" r="10" fill="#13c892" />
		<path d="M5.5 10l3.5 3.5 5.5-7" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

// ── Data ──────────────────────────────────────────────────────────────────────

const getFeatureOverviewItems = (translations: Record<string, string>) => [
	{
		title: t(translations, "itineraryPlanner.featureOverview.items.routeMap.title", "Interactive route map"),
		description: t(
			translations,
			"itineraryPlanner.featureOverview.items.routeMap.description",
			"Every stop pinned and connected. See your full route at a glance."
		),
		icon: <Icon name="map--outlined" size={22} fill="currentColor" />,
	},
	{
		title: t(translations, "itineraryPlanner.featureOverview.items.calendar.title", "Calendar view"),
		description: t(
			translations,
			"itineraryPlanner.featureOverview.items.calendar.description",
			"See your whole trip in a calendar: days, stays and activities laid out clearly."
		),
		icon: <IconCalendar />,
	},
	{
		title: t(translations, "itineraryPlanner.featureOverview.items.timeline.title", "Day-by-day timeline"),
		description: t(
			translations,
			"itineraryPlanner.featureOverview.items.timeline.description",
			"Plan every day in detail: drag to reorder, nothing gets lost."
		),
		icon: <IconList />,
	},
	{
		title: t(translations, "itineraryPlanner.featureOverview.items.transport.title", "Transport planning"),
		description: t(
			translations,
			"itineraryPlanner.featureOverview.items.transport.description",
			"Flights, trains, buses, all logged in your itinerary timeline automatically."
		),
		icon: <IconTrain />,
	},
	{
		title: t(translations, "itineraryPlanner.featureOverview.items.accommodation.title", "Accommodation"),
		description: t(
			translations,
			"itineraryPlanner.featureOverview.items.accommodation.description",
			"Search Booking.com, Airbnb and Hostelworld directly inside the planner."
		),
		icon: <IconBuilding />,
	},
	{
		title: t(translations, "itineraryPlanner.featureOverview.items.activities.title", "Activities & places"),
		description: t(
			translations,
			"itineraryPlanner.featureOverview.items.activities.description",
			"Discover things to do and add them straight to your itinerary."
		),
		icon: <IconLocation />,
	},
];

const getTripTypes = (translations: Record<string, string>) => {
	const tagDayByDay = t(translations, "itineraryPlanner.tripTypes.tags.dayByDay", "Day-by-day");
	const tagPackingList = t(translations, "itineraryPlanner.tripTypes.tags.packingList", "Packing list");
	const tagShareableLink = t(translations, "itineraryPlanner.tripTypes.tags.shareableLink", "Shareable link");
	const tagSharedNotes = t(translations, "itineraryPlanner.tripTypes.tags.sharedNotes", "Shared notes");

	return [
		{
			title: t(translations, "itineraryPlanner.tripTypes.backpacking.title", "Backpacking trips"),
			subtitle: t(translations, "itineraryPlanner.tripTypes.backpacking.subtitle", "Multi-stop · Budget-friendly"),
			tags: [tagDayByDay, tagPackingList, tagShareableLink],
			image: BackpackingVisual,
		},
		{
			title: t(translations, "itineraryPlanner.tripTypes.family.title", "Family holidays"),
			subtitle: t(translations, "itineraryPlanner.tripTypes.family.subtitle", "Kid-friendly · All organised"),
			tags: [tagDayByDay, tagPackingList, tagSharedNotes],
			image: FamilyTripVisual,
		},
		{
			title: t(translations, "itineraryPlanner.tripTypes.group.title", "Group travel"),
			subtitle: t(translations, "itineraryPlanner.tripTypes.group.subtitle", "Collaborative · Easy to share"),
			tags: [tagSharedNotes, tagPackingList, tagShareableLink],
			image: GroupTravelVisual,
		},
		{
			title: t(translations, "itineraryPlanner.tripTypes.roadTrip.title", "Road trips"),
			subtitle: t(translations, "itineraryPlanner.tripTypes.roadTrip.subtitle", "Flexible · Day by day"),
			tags: [tagDayByDay, tagSharedNotes, tagPackingList],
			image: RoadTripVisual,
		},
	];
};

// ── Component ─────────────────────────────────────────────────────────────────

interface ItineraryPlannerPageProps {
	lang?: string;
}

const ItineraryPlannerPage = ({ lang }: ItineraryPlannerPageProps) => {
	const translations = getItineraryPlannerTranslations(lang || "en");
	const FEATURE_OVERVIEW_ITEMS = getFeatureOverviewItems(translations);
	const TRIP_TYPES = getTripTypes(translations);
	const [user] = useAuthState(firebase.auth);
	const { user: stipplUser, initializing } = useUser();
	const { history, match } = useReactRouter();
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const { isBusinessMode: isBusiness } = useBusinessContext();
	const { organisation, loading: orgLoading } = useOrganisation();
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	React.useEffect(() => {
		if (!user || !stipplUser?.createdAt) return;
		if (isBusiness) {
			if (orgLoading) return;
			const orgName = organisation?.name;
			if (orgName) history.push(`/${encodeURIComponent(orgName)}`);
			return;
		}
		const hasValidUsername = !!stipplUser?.username && !stipplUser?.username?.includes("StipplUser");
		if (hasValidUsername) {
			history.push("/home");
		} else {
			history.push("/onboarding");
		}
	}, [user, initializing, stipplUser, isBusiness, organisation, orgLoading]);

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: both badges resolve to the same attributed OneLink, same as AppStoreButtons --
	// no user-facing download link may ever be a direct apps.apple.com/play.google.com URL, so
	// there's no separate desktop fallback to pick per badge anymore. See useAppDownloadUrl's doc
	// comment in useAppStoreLink.ts, including the known desktop-Play-Store trade-off.
	const appDownloadUrl = useAppDownloadUrl();

	return (
		<div id="stippl-homepage">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* ── HERO ── */}
			<section className="hr-hero">
				<div className="container__inner hr-hero__inner">
					<div className="hr-hero__content">
						<span className="hr-hero__badge">{t(translations, "itineraryPlanner.hero.badge", "Free itinerary planner")}</span>
						<h1>
							{t(translations, "itineraryPlanner.hero.titlePrefix", "The ")}
							<span className="hr-hero__accent">{t(translations, "itineraryPlanner.hero.titleAccent", "itinerary planner")}</span>
							{t(translations, "itineraryPlanner.hero.titleSuffix", " for trips")}
							<br />
							{t(translations, "itineraryPlanner.hero.titleLine2", "worth remembering")}
						</h1>
						<p className="hr-hero__desc">
							{t(
								translations,
								"itineraryPlanner.hero.description",
								"Build a beautiful day-by-day travel itinerary: with your route on a map, your stays and activities all in one place. Free forever, for any trip."
							)}
						</p>
						<div className="hr-hero__ctas">
							{/* 123yb6937h3 (QA retest): this was a plain <button onClick={setShowDownloadModal(true)}>
								with no href at all -- QA's curl of production found a real <button> at 215x56px in
								the server HTML with hasAttribute("href") === false, and a CDP-throttled hydration
								check showed the same dead control survives ~3s into a slow load. It's a real
								<a href={appDownloadUrl}> now: appDownloadUrl (useAppDownloadUrl, useAppStoreLink.ts)
								always resolves a real, attributed OneLink, including on the very first render before
								mount -- see its doc comment for why that's safe for hydration -- so the server HTML
								never ships without an href again. On a phone the anchor is simply followed: OneLink
								does the OS split (App Store vs Play Store) server-side, which is the ticket's
								acceptance criterion. On desktop we still want DownloadAppModal's QR code as the
								affordance, so the click handler -- browser-only, unlike a render-time device check --
								prevents the default navigation and opens the modal instead. */}
							<a
								href={appDownloadUrl}
								className="hr-hero__download-btn"
								onClick={(e) => {
									handleTrack("download");
									if (!isMobile) {
										e.preventDefault();
										setShowDownloadModal(true);
									}
								}}
							>
								<span className="hr-hero__download-btn__logo">
									<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
										<path d={S_PATH} fill="#fff" />
									</svg>
								</span>
								{t(translations, "itineraryPlanner.hero.downloadBtn", "Download the app")}
							</a>
							<a
								href="/register"
								className="hr-hero__web-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "itineraryPlanner.hero.webLink", "Start on web →")}
							</a>
						</div>
					</div>
					<img
						className="hr-hero__phone hr-hero__phone--front"
						src={PlannerMapVisual}
						alt={t(translations, "itineraryPlanner.hero.imageAlt", "Stippl planner")}
						loading="eager"
					/>
				</div>
			</section>

			{/* ── FEATURE OVERVIEW ── */}
			<section className="hr-feature-section hr-feature-overview">
				<div className="container__inner">
					<span className="hr-pill">{t(translations, "itineraryPlanner.featureOverview.pill", "Free travel itinerary planner")}</span>
					<h2>{t(translations, "itineraryPlanner.featureOverview.heading", "Your day-by-day itinerary, every detail in place")}</h2>
					<p className="hr-feature-overview__desc">
						{t(
							translations,
							"itineraryPlanner.featureOverview.description",
							"Stippl is a free online travel itinerary planner. Add flights, hotels, activities and transport to a structured day-by-day plan, see your whole route on a map, and share it with everyone you're travelling with: whether it's a city break, backpacking trip, family holiday or road trip."
						)}
					</p>
					<div className="hr-feature-overview__grid">
						{FEATURE_OVERVIEW_ITEMS.map((item, i) => (
							<a key={i}
								href="/register"
								className="hr-feature-card"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								<div className="hr-feature-card__icon">{item.icon}</div>
								<h4>{item.title}</h4>
								<p>{item.description}</p>
							</a>
						))}
					</div>
				</div>
			</section>

			{/* ── ROUTE PLANNING ── */}
			<section className="hr-feature-section hr-feature-section--tinted">
				<div className="container__inner hr-feature-section__inner">
					<div className="hr-feature-section__text">
						<span className="hr-pill">{t(translations, "itineraryPlanner.routePlanning.pill", "Route planning")}</span>
						<h2>{t(translations, "itineraryPlanner.routePlanning.heading", "Your entire route, mapped and ordered")}</h2>
						<p>
							{t(
								translations,
								"itineraryPlanner.routePlanning.description",
								"Add your destinations in any order, Stippl maps your full route automatically and puts everything on an interactive timeline. Multi-city, multi-country, multi-week: the itinerary planner scales to any trip."
							)}
						</p>
						<ul className="hr-bullets">
							<li>
								<span className="hr-bullets__icon">
									<Icon name="marker--outlined" fill="white" size={16} />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.routePlanning.bullet1Title", "Interactive route map")}</strong>
									<span>
										{t(
											translations,
											"itineraryPlanner.routePlanning.bullet1Text",
											"Every stop pinned and connected, distances and travel times calculated automatically."
										)}
									</span>
								</div>
							</li>
							<li>
								<span className="hr-bullets__icon">
									<IcoReorder />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.routePlanning.bullet2Title", "Drag to reorder")}</strong>
									<span>
										{t(translations, "itineraryPlanner.routePlanning.bullet2Text", "Changed your mind? Drag to reorder and everything updates instantly.")}
									</span>
								</div>
							</li>
							<li>
								<span className="hr-bullets__icon">
									<IcoInfinity />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.routePlanning.bullet3Title", "Unlimited stops")}</strong>
									<span>{t(translations, "itineraryPlanner.routePlanning.bullet3Text", "30 days, 10 countries, no limits on stops or days.")}</span>
								</div>
							</li>
						</ul>
						<a
							href="/register"
							className="hr-text-link"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "itineraryPlanner.routePlanning.cta", "Start mapping your trip →")}
						</a>
					</div>
					<div className="hr-feature-section__visual">
						<img
							src={FeatureRouteVisual}
							alt={t(translations, "itineraryPlanner.routePlanning.imageAlt", "Route planning on Stippl")}
							className="hr-feature-section__img"
							loading="lazy"
						/>
					</div>
				</div>
			</section>

			{/* ── DAY-BY-DAY ── */}
			<section className="hr-feature-section hr-feature-section--alt hr-feature-section--white">
				<div className="container__inner hr-feature-section__inner">
					<div className="hr-feature-section__visual">
						<img
							src={FeatureDayVisual}
							alt={t(translations, "itineraryPlanner.dayByDay.imageAlt", "Day-by-day planner on Stippl")}
							className="hr-feature-section__img"
							loading="lazy"
						/>
					</div>
					<div className="hr-feature-section__text">
						<span className="hr-pill">{t(translations, "itineraryPlanner.dayByDay.pill", "Day-by-day planner")}</span>
						<h2>{t(translations, "itineraryPlanner.dayByDay.heading", "Every day planned, nothing forgotten")}</h2>
						<p>
							{t(
								translations,
								"itineraryPlanner.dayByDay.description",
								"Build your trip day by day. Add stays, activities, restaurants and transport to each day of your holiday planner, then rearrange whenever plans change. Your travel itinerary is always up to date."
							)}
						</p>
						<ul className="hr-bullets">
							<li>
								<span className="hr-bullets__icon">
									<IconCalendar />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.dayByDay.bullet1Title", "Book stays inside the planner")}</strong>
									<span>{t(translations, "itineraryPlanner.dayByDay.bullet1Text", "Search Booking.com, Airbnb and Hostelworld directly inside Stippl.")}</span>
								</div>
							</li>
							<li>
								<span className="hr-bullets__icon">
									<Icon name="search" size={16} fill="currentColor" />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.dayByDay.bullet2Title", "Discover things to do")}</strong>
									<span>{t(translations, "itineraryPlanner.dayByDay.bullet2Text", "Curated activities and restaurants for every destination.")}</span>
								</div>
							</li>
							<li>
								<span className="hr-bullets__icon">
									<IcoPlane />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.dayByDay.bullet3Title", "Add transport between stops")}</strong>
									<span>{t(translations, "itineraryPlanner.dayByDay.bullet3Text", "Flights, trains, buses, it all appears in your itinerary timeline.")}</span>
								</div>
							</li>
						</ul>
						<a
							href="/register"
							className="hr-text-link"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "itineraryPlanner.dayByDay.cta", "Build your first itinerary →")}
						</a>
					</div>
				</div>
			</section>

			{/* ── WEB & MOBILE ── */}
			<section className="hr-feature-section hr-feature-section--tinted">
				<div className="container__inner hr-feature-section__inner">
					<div className="hr-feature-section__text">
						<span className="hr-pill">{t(translations, "itineraryPlanner.webMobile.pill", "Web & mobile")}</span>
						<h2>{t(translations, "itineraryPlanner.webMobile.heading", "Plan on your laptop. Live it on your phone.")}</h2>
						<p>
							{t(
								translations,
								"itineraryPlanner.webMobile.description",
								"Plan on web, on your phone, or both, Stippl works however you like. Most people find it easiest to build out the full trip on a big screen first, then switch to the app when they're on the road."
							)}
						</p>
						<ul className="hr-bullets">
							<li>
								<span className="hr-bullets__icon">
									<IcoMonitor />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.webMobile.bullet1Title", "Full planner in your browser")}</strong>
									<span>{t(translations, "itineraryPlanner.webMobile.bullet1Text", "Build your entire itinerary on web, no download needed.")}</span>
								</div>
							</li>
							<li>
								<span className="hr-bullets__icon">
									<IcoSync />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.webMobile.bullet2Title", "Instant sync across devices")}</strong>
									<span>
										{t(translations, "itineraryPlanner.webMobile.bullet2Text", "Add a stop on your laptop and it's on your phone before you close the tab.")}
									</span>
								</div>
							</li>
							<li>
								<span className="hr-bullets__icon">
									<IcoPhone />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.webMobile.bullet3Title", "Offline access on mobile")}</strong>
									<span>
										{t(translations, "itineraryPlanner.webMobile.bullet3Text", "Every stop, note and booking saved to your device, no Wi-Fi needed.")}
									</span>
								</div>
							</li>
						</ul>
						<div className="hr-appstore-btns">
							<a href={appDownloadUrl} target="_blank" rel="noreferrer" onClick={() => handleTrack("appstore")}>
								<img
									src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
									alt={t(translations, "itineraryPlanner.webMobile.appStoreAlt", "Download on the App Store")}
								/>
							</a>
							<a href={appDownloadUrl} target="_blank" rel="noreferrer" onClick={() => handleTrack("googleplay")}>
								<img
									src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
									alt={t(translations, "itineraryPlanner.webMobile.googlePlayAlt", "Get it on Google Play")}
								/>
							</a>
						</div>
					</div>
					<div className="hr-feature-section__visual">
						<img
							src={FeatureWebMobileVisual}
							alt={t(translations, "itineraryPlanner.webMobile.imageAlt", "Stippl web planner")}
							className="hr-feature-section__img"
							loading="lazy"
						/>
					</div>
				</div>
			</section>

			{/* ── GROUP TRAVEL ── */}
			<section className="hr-feature-section hr-feature-section--alt hr-feature-section--white">
				<div className="container__inner hr-feature-section__inner">
					<div className="hr-feature-section__visual">
						<img
							src={FeatureGroupVisual}
							alt={t(translations, "itineraryPlanner.groupTravel.imageAlt", "Group travel planning on Stippl")}
							className="hr-feature-section__img"
							loading="lazy"
						/>
					</div>
					<div className="hr-feature-section__text">
						<span className="hr-pill">{t(translations, "itineraryPlanner.groupTravel.pill", "Group travel planning")}</span>
						<h2>{t(translations, "itineraryPlanner.groupTravel.heading", "Plan your group trip, without the chaos")}</h2>
						<p>
							{t(
								translations,
								"itineraryPlanner.groupTravel.description",
								"Stippl is a free group trip planner that lets everyone contribute in real time. Invite friends or family, collect ideas, vote on activities and land on a shared itinerary, without the endless group chat back-and-forth."
							)}
						</p>
						<ul className="hr-bullets">
							<li>
								<span className="hr-bullets__icon">
									<IcoUsers />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.groupTravel.bullet1Title", "One shared itinerary for the whole group")}</strong>
									<span>
										{t(translations, "itineraryPlanner.groupTravel.bullet1Text", "Everyone can view, suggest and edit in real time, no version chaos.")}
									</span>
								</div>
							</li>
							<li>
								<span className="hr-bullets__icon">
									<IcoStar />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.groupTravel.bullet2Title", "Gather and vote on ideas")}</strong>
									<span>{t(translations, "itineraryPlanner.groupTravel.bullet2Text", "Suggest, vote, decide. No more 37 unread messages.")}</span>
								</div>
							</li>
							<li>
								<span className="hr-bullets__icon">
									<Icon name="upload" size={16} fill="currentColor" />
								</span>
								<div>
									<strong>{t(translations, "itineraryPlanner.groupTravel.bullet3Title", "Shared notes and documents")}</strong>
									<span>{t(translations, "itineraryPlanner.groupTravel.bullet3Text", "Packing lists, visa info, contacts, all in one place.")}</span>
								</div>
							</li>
						</ul>
						<a
							href="/register"
							className="hr-text-link"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "itineraryPlanner.groupTravel.cta", "Plan your group trip →")}
						</a>
					</div>
				</div>
			</section>

			{/* ── AI PLANNER ── */}
			<section className="hr-feature-section hr-feature-section--alt hr-feature-section--ai">
				<div className="container__inner hr-feature-section__inner">
					<div className="hr-feature-section__visual">
						<img
							src={FeatureAiVisual}
							alt={t(translations, "itineraryPlanner.aiPlanner.imageAlt", "AI trip generator on Stippl")}
							className="hr-feature-section__img"
							loading="lazy"
						/>
					</div>
					<div className="hr-feature-section__text hr-ai-text">
						<span className="hr-pill-outlined hr-pill-outlined--purple">{t(translations, "itineraryPlanner.aiPlanner.pill", "AI travel planner")}</span>
						<h2>
							{t(translations, "itineraryPlanner.aiPlanner.headingLine1", "Your trip, planned by AI.")}
							<br />
							{t(translations, "itineraryPlanner.aiPlanner.headingLine2", "Perfected by you.")}
						</h2>
						<p>
							{t(
								translations,
								"itineraryPlanner.aiPlanner.description",
								"Not sure where to start? Stippl's AI travel planner generates a personalised day-by-day itinerary in seconds, then lets you tweak every detail until it's exactly right. It's the fastest way from blank page to trip of a lifetime."
							)}
						</p>
						<ul className="hr-ai-bullets">
							<li>
								<div className="hr-ai-bullets__icon">
									<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
										<path d="M8 1L9.2 4.4L13 6L9.2 7.6L8 11L6.8 7.6L3 6L6.8 4.4L8 1Z" stroke="white" strokeWidth="1.3" strokeLinejoin="round" fill="none" />
										<path d="M2 2L2.5 3L3 2L2.5 1L2 2Z" stroke="white" strokeWidth="1" strokeLinejoin="round" fill="none" />
										<path
											d="M13 11L13.5 12.5L15 13L13.5 13.5L13 15L12.5 13.5L11 13L12.5 12.5L13 11Z"
											stroke="white"
											strokeWidth="1"
											strokeLinejoin="round"
											fill="none"
										/>
									</svg>
								</div>
								<div>
									<strong>{t(translations, "itineraryPlanner.aiPlanner.bullet1Title", "Generate a full itinerary in seconds")}</strong>
									<p>
										{t(
											translations,
											"itineraryPlanner.aiPlanner.bullet1Text",
											"Tell the AI where you're going, for how long, and what you're into, it builds a complete day-by-day plan instantly."
										)}
									</p>
								</div>
							</li>
							<li>
								<div className="hr-ai-bullets__icon">
									<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
										<path d="M11 2L14 5L5 14H2V11L11 2Z" stroke="white" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" fill="none" />
										<path d="M9 4L12 7" stroke="white" strokeWidth="1.3" strokeLinecap="round" fill="none" />
									</svg>
								</div>
								<div>
									<strong>{t(translations, "itineraryPlanner.aiPlanner.bullet2Title", "Edit it like any other itinerary")}</strong>
									<p>
										{t(
											translations,
											"itineraryPlanner.aiPlanner.bullet2Text",
											"Once generated, every day, activity and note is fully yours to move, swap, delete or build on, no AI lock-in."
										)}
									</p>
								</div>
							</li>
							<li>
								<div className="hr-ai-bullets__icon">
									<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
										<path d="M8 2V11M8 11L5 8M8 11L11 8" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
										<path d="M2 13H14" stroke="white" strokeWidth="1.3" strokeLinecap="round" fill="none" />
									</svg>
								</div>
								<div>
									<strong>{t(translations, "itineraryPlanner.aiPlanner.bullet3Title", "Saved straight to your trip")}</strong>
									<p>
										{t(
											translations,
											"itineraryPlanner.aiPlanner.bullet3Text",
											"Every AI suggestion lands directly in your itinerary, ready to edit, share or take offline."
										)}
									</p>
								</div>
							</li>
						</ul>
						<div className="hr-ai-cta">
							<a
								href="/register"
								className="hr-ai-cta__btn"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "itineraryPlanner.aiPlanner.ctaBtn", "Try the AI planner →")}
							</a>
							<a href={localizeHref("/ai-travel-planner", lang as MarketingLangCode)} className="hr-ai-cta__link">
								{t(translations, "itineraryPlanner.aiPlanner.ctaLink", "See how it works")}
							</a>
						</div>
					</div>
				</div>
			</section>

			{/* ── HOW IT WORKS ── */}
			<HowItWorksSection
				theme="green"
				title={t(translations, "itineraryPlanner.howToUse.name", ITINERARY_PLANNER_CONTENT.howToUse.name)}
				description={t(translations, "itineraryPlanner.howToUse.description", ITINERARY_PLANNER_CONTENT.howToUse.description)}
				steps={ITINERARY_PLANNER_CONTENT.howToSteps.map((step, index) => ({
					...step,
					name: t(translations, `itineraryPlanner.howToSteps.${index}.name`, step.name),
					text: t(translations, `itineraryPlanner.howToSteps.${index}.text`, step.text),
				}))}
				ctaText={t(translations, "itineraryPlanner.howItWorks.ctaText", "Start planning for free →")}
				onCtaClick={() => handleTrack("signup")}
			/>

			<section className="hr-feature-section">
				<TestimonialsSection />
			</section>

			{/* ── PRO PRICING ── */}
			<section className="hr-feature-section hr-pricing">
				<div className="container__inner">
					<div className="hr-pricing__card">
						<div className="hr-pricing__left">
							<span className="hr-pill-outlined hr-pricing__pill">
								{/* <svg width="9" height="12" viewBox="0 0 9 12" fill="#13c892" style={{ flexShrink: 0 }}>
									<path d={S_PATH} />
								</svg> */}
								<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="var(--color-primary)" />
								</svg>
								STIPPL. PRO
							</span>
							<h2>{t(translations, "itineraryPlanner.pricing.heading", "Your itinerary planner, with superpowers")}</h2>
							<p>
								{t(
									translations,
									"itineraryPlanner.pricing.description",
									"The planner is free forever. Pro unlocks the features that turn planning from hours into seconds."
								)}
							</p>
							<ul className="hr-pricing__features">
								<li>
									<IcoCheck />
									<span>
										<strong>{t(translations, "itineraryPlanner.pricing.feature1Title", "Unlimited AI itinerary generation")}</strong>:{" "}
										{t(translations, "itineraryPlanner.pricing.feature1Text", "full day-by-day trip plan in 60 seconds for any destination")}
									</span>
								</li>
								<li>
									<IcoCheck />
									<span>
										<strong>{t(translations, "itineraryPlanner.pricing.feature2Title", "AI day-plan generator")}</strong>:{" "}
										{t(translations, "itineraryPlanner.pricing.feature2Text", "instant day plan for any city, tailored to your style and pace")}
									</span>
								</li>
								<li>
									<IcoCheck />
									<span>
										<strong>{t(translations, "itineraryPlanner.pricing.feature3Title", "PDF export")}</strong>:{" "}
										{t(translations, "itineraryPlanner.pricing.feature3Text", "share your itinerary with travel partners or save offline")}
									</span>
								</li>
								<li>
									<IcoCheck />
									<span>
										<strong>{t(translations, "itineraryPlanner.pricing.feature4Title", "Calendar sync")}</strong>:{" "}
										{t(translations, "itineraryPlanner.pricing.feature4Text", "your trip, in your calendar, synced in real time")}
									</span>
								</li>
							</ul>
						</div>
						<div className="hr-pricing__right">
							<p className="hr-pricing__plan-label">{t(translations, "itineraryPlanner.pricing.planLabel", "Pro Plan")}</p>
							<div className="hr-pricing__amount">€5</div>
							<span className="hr-pricing__period">{t(translations, "itineraryPlanner.pricing.period", "per month")}</span>
							<a href={localizeHref("/pro", lang as MarketingLangCode)} className="hr-pricing__get-btn">
								<svg width="14" height="18" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="currentColor" />
								</svg>
								&nbsp; {t(translations, "itineraryPlanner.pricing.getBtn", "Get Pro →")}
							</a>
							<p className="hr-pricing__note">{t(translations, "itineraryPlanner.pricing.note", "cancel anytime")}</p>
							<div className="hr-pricing__hero">
								<svg
									width="520"
									height="680"
									viewBox="0 0 338 454"
									fill="none"
									data-fg-dzzi456="92.484:126.467:/src/app/components/SimplifiedFeaturePage.tsx:916:17:58402:824:e:svg:e"
									data-fgid-dzzi456=":rld:"
								>
									<path
										d="M73.1074 416.95C62.7264 416.95 54.4857 425.243 54.687 435.475C54.8958 445.7 63.4721 454 73.8457 454C84.2268 454 92.4675 445.7 92.2662 435.475C92.0574 425.243 83.481 416.95 73.1074 416.95ZM274.643 140.241C320.001 128.182 338.071 101.327 338.108 70.3779C338.16 28.3988 304.146 0 248.385 0C160.556 0 0 94.5184 0 181.258C0 249.295 88.4404 271.369 148.393 285.42C208.352 299.462 254.694 312.222 254.694 328.808C254.694 345.387 201.812 382.757 116.638 409.784C107.51 412.819 102.177 420.977 104.713 428.003C107.256 435.028 116.504 437.519 125.841 435.222C161.317 426.474 328.734 388.201 328.734 312.841C328.734 237.482 148.333 206.659 148.333 192.952C148.333 178.879 237.937 149.996 274.643 140.241Z"
										fill="white"
										data-fg-dzzi457="92.484:126.467:/src/app/components/SimplifiedFeaturePage.tsx:917:19:58485:718:e:path"
									></path>
								</svg>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ── TRIP TYPES ── */}
			<section className="hr-feature-section hr-trip-types">
				<div className="container__inner">
					<span className="hr-pill">{t(translations, "itineraryPlanner.tripTypes.pill", "Every kind of trip")}</span>
					<h2>{t(translations, "itineraryPlanner.tripTypes.heading", "The itinerary planner that works for any trip")}</h2>
					<div className="hr-trip-types__grid">
						{TRIP_TYPES.map((type, i) => (
							<div key={i} className="hr-trip-card">
								<div className="hr-trip-card__image">
									<img src={type.image} alt={type.title} />
								</div>
								<div className="hr-trip-card__body">
									<h4>{type.title}</h4>
									<p className="hr-trip-card__subtitle">{type.subtitle}</p>
									<div className="hr-trip-card__tags">
										{type.tags.map((tag, j) => (
											<span key={j} className="hr-trip-card__tag">
												{tag}
											</span>
										))}
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
				{/* ── B2B ── */}
				<div className="hr-b2b">
					<div className="container__inner">
						<div className="hr-b2b__card">
							<div className="hr-b2b__icon">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="white"
									stroke-width="1.75"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
									<rect width="20" height="14" x="2" y="6" rx="2"></rect>
								</svg>
							</div>
							<div className="hr-b2b__content">
								<h3>{t(translations, "itineraryPlanner.b2b.heading", "The full tech stack for your travel business")}</h3>
								<p>
									{t(
										translations,
										"itineraryPlanner.b2b.description",
										"White-label Stippl for your agency or tour operation: branded itineraries, client portals, Travel Reels and booking tools, all under your name."
									)}
								</p>
							</div>
							<a href="/for-travel-agents" className="btn-rounded solid hr-b2b__btn">
								{t(translations, "itineraryPlanner.b2b.cta", "Learn more →")}
							</a>
							<div data-fg-dzzi515="92.484:126.467:/src/app/components/SimplifiedFeaturePage.tsx:1101:11:67751:169:e:div" data-fgid-dzzi515=":rmv:"></div>
						</div>
					</div>
				</div>
			</section>

			{/* ── FAQ ── */}
			<section className="hr-faq">
				<div className="container__inner">
					<FAQSection
						heading={t(translations, "itineraryPlanner.faq.heading", "Frequently asked questions about our itinerary planner")}
						items={ITINERARY_PLANNER_CONTENT.faqItems.map((item, index) => ({
							...item,
							question: t(translations, `itineraryPlanner.faqItems.${index}.question`, item.question),
							answer: t(translations, `itineraryPlanner.faqItems.${index}.answer`, item.answer),
						}))}
					/>
				</div>
			</section>

			<Footer />
			<DownloadAppQrCard />
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
};

export default ItineraryPlannerPage;
